// src/pages/Photos/PhotosPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiCamera,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";

import {
  getAllPhotosByCompetitionId,
  addPhoto,
  updatePhoto,
  deletePhoto,
} from "../../services/photosServices";
import { getUserCompetitions } from "../../services/competitionsServices";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";

import PodiumPhotoDrawer from "./PodiumPhotoDrawer";

const FALLBACK_IMAGE = "/img/no-image.jpg";

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

const CARD =
  "rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (String(photoUrl).startsWith("http")) return photoUrl;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const clean = String(photoUrl).replaceAll("\\", "/").replace(/^\/+/, "");

  return `${base}/${clean}`;
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const formatDateFR = (value) => {
  if (!value) return "Date non renseignée";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export default function PhotosPage() {
  useScrollToTop();

  const { user } = useUser();
  const userId = user?.userId ?? user?.id ?? user?.user_id ?? null;

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState("");

  const [photos, setPhotos] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  const [q, setQ] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editingPhoto, setEditingPhoto] = useState(null);

  const [enlargedImage, setEnlargedImage] = useState(null);

  const selectedCompetition = useMemo(() => {
    return competitions.find(
      (competition) => Number(competition.id) === Number(selectedCompetitionId)
    );
  }, [competitions, selectedCompetitionId]);

  const filteredPhotos = useMemo(() => {
    const search = normalizeText(q.trim());

    if (!search) return photos;

    return photos.filter((photo) => {
      const searchable = [photo.description, photo.photo_url, photo.id]
        .map(normalizeText)
        .join(" ");

      return searchable.includes(search);
    });
  }, [photos, q]);

  const fetchCompetitions = useCallback(async () => {
    if (!userId) {
      setCompetitions([]);
      setLoadingCompetitions(false);
      return;
    }

    try {
      setLoadingCompetitions(true);

      const data = await getUserCompetitions(userId);
      const rows = Array.isArray(data) ? data : [];

      setCompetitions(rows);

      if (!selectedCompetitionId && rows.length > 0) {
        setSelectedCompetitionId(String(rows[0].id));
      }
    } catch (error) {
      console.error("[PhotosPage] fetchCompetitions error:", error);
      toast.error("Erreur lors du chargement des compétitions.");
    } finally {
      setLoadingCompetitions(false);
    }
  }, [userId, selectedCompetitionId]);

  const fetchPhotos = useCallback(async () => {
    if (!selectedCompetitionId) {
      setPhotos([]);
      return;
    }

    try {
      setLoadingPhotos(true);

      const data = await getAllPhotosByCompetitionId(selectedCompetitionId);
      setPhotos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[PhotosPage] fetchPhotos error:", error);
      toast.error("Erreur lors du chargement des photos.");
    } finally {
      setLoadingPhotos(false);
    }
  }, [selectedCompetitionId]);

  useEffect(() => {
    fetchCompetitions();
  }, [fetchCompetitions]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const openCreateDrawer = () => {
    if (!selectedCompetitionId) {
      toast.info("Sélectionnez d’abord une compétition.");
      return;
    }

    setDrawerMode("create");
    setEditingPhoto(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (photo) => {
    setDrawerMode("edit");
    setEditingPhoto(photo);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode("create");
    setEditingPhoto(null);
  };

  const handleSave = async (payload) => {
    if (!selectedCompetitionId) {
      toast.error("Aucune compétition sélectionnée.");
      return;
    }

    const photoUrl = String(payload?.photo_url || "").trim();

    if (!photoUrl) {
      toast.error("L’URL de la photo est obligatoire.");
      return;
    }

    try {
      if (drawerMode === "edit") {
        if (!editingPhoto?.id) return;

        await updatePhoto(editingPhoto.id, {
          photo_url: photoUrl,
          description: String(payload?.description || "").trim(),
          competition_id: Number(selectedCompetitionId),
        });

        toast.success("Photo modifiée.");
      } else {
        await addPhoto({
          photo_url: photoUrl,
          description: String(payload?.description || "").trim(),
          competition_id: Number(selectedCompetitionId),
        });

        toast.success("Photo ajoutée.");
      }

      closeDrawer();
      await fetchPhotos();
    } catch (error) {
      console.error("[PhotosPage] handleSave error:", error);
      toast.error("Erreur lors de l’enregistrement de la photo.");
    }
  };

  const handleDelete = async (photo) => {
    const ok = window.confirm("Supprimer cette photo de podium ?");
    if (!ok) return;

    try {
      await deletePhoto(photo.id);
      toast.success("Photo supprimée.");
      await fetchPhotos();
    } catch (error) {
      console.error("[PhotosPage] handleDelete error:", error);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const resetFilters = () => {
    setQ("");
  };

  return (
    <div className={PAGE}>
      <ScrollIndicator />

      <main className="mx-auto w-full max-w-[1680px] px-3 pb-12 pt-[130px] sm:px-5 md:pt-[155px]">
        <div className="space-y-6">
          <PhotosHero
            selectedCompetition={selectedCompetition}
            photosCount={photos.length}
            filteredCount={filteredPhotos.length}
            loading={loadingPhotos || loadingCompetitions}
            onRefresh={() => {
              fetchCompetitions();
              fetchPhotos();
            }}
            onCreate={openCreateDrawer}
          />

          <CompetitionSelector
            competitions={competitions}
            selectedCompetitionId={selectedCompetitionId}
            setSelectedCompetitionId={(value) => {
              setSelectedCompetitionId(value);
              setQ("");
            }}
            loading={loadingCompetitions}
          />

          {selectedCompetitionId ? (
            <>
              <PhotosToolbar
                q={q}
                setQ={setQ}
                total={filteredPhotos.length}
                onReset={resetFilters}
                onCreate={openCreateDrawer}
              />

              {loadingPhotos ? (
                <PhotosLoadingGrid />
              ) : filteredPhotos.length === 0 ? (
                <EmptyState
                  hasPhotos={photos.length > 0}
                  onCreate={openCreateDrawer}
                  onReset={resetFilters}
                />
              ) : (
                <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {filteredPhotos.map((photo) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      onEdit={openEditDrawer}
                      onDelete={handleDelete}
                      onPreview={(url) => setEnlargedImage(url)}
                    />
                  ))}
                </section>
              )}
            </>
          ) : (
            <NoCompetitionState />
          )}
        </div>
      </main>

      <PodiumPhotoDrawer
        open={drawerOpen}
        mode={drawerMode}
        initial={editingPhoto}
        selectedCompetition={selectedCompetition}
        onClose={closeDrawer}
        onSave={handleSave}
      />

      {enlargedImage ? (
        <Lightbox imageUrl={enlargedImage} onClose={() => setEnlargedImage(null)} />
      ) : null}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

function PhotosHero({
  selectedCompetition,
  photosCount,
  filteredCount,
  loading,
  onRefresh,
  onCreate,
}) {
  return (
    <section className="overflow-hidden rounded-[36px] bg-slate-950 shadow-xl dark:bg-slate-900">
      <div className="relative p-5 sm:p-7 lg:p-8">
        <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-indigo-500/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-blue-500/15 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
              <FiCamera />
              Photos des podiums
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Gestion des photos de podiums
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
              Ajoutez les photos prises lors de la compétition. Elles pourront
              être affichées sur l’espace public des résultats selon votre
              configuration.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCreate}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100"
            >
              <FiPlus />
              Ajouter une photo
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/15 transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="relative mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <HeroStat icon={FiImage} label="Photos" value={photosCount} />
          <HeroStat icon={FiSearch} label="Affichées" value={filteredCount} />
          <HeroStat
            icon={FiCamera}
            label="Compétition"
            value={selectedCompetition ? "Sélectionnée" : "Aucune"}
          />
        </div>
      </div>
    </section>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 text-white shadow-sm backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
          <Icon />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-white/60">
            {label}
          </div>
          <div className="mt-0.5 text-2xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function CompetitionSelector({
  competitions,
  selectedCompetitionId,
  setSelectedCompetitionId,
  loading,
}) {
  const selected = competitions.find(
    (competition) => Number(competition.id) === Number(selectedCompetitionId)
  );

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="p-4 sm:p-5">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Choisir une compétition
          </h2>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Les photos seront rattachées à la compétition sélectionnée.
          </p>

          <select
            className={`${INPUT} mt-4`}
            value={selectedCompetitionId || ""}
            onChange={(event) => setSelectedCompetitionId(event.target.value)}
            disabled={loading}
          >
            <option value="">
              {loading
                ? "Chargement des compétitions..."
                : "-- Choisir une compétition --"}
            </option>

            {competitions.map((competition) => (
              <option key={competition.id} value={competition.id}>
                {competition.nom_competition}
              </option>
            ))}
          </select>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60 lg:border-l lg:border-t-0 sm:p-5">
          {selected ? (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Compétition active
              </div>

              <h3 className="mt-2 line-clamp-2 text-lg font-bold text-slate-900 dark:text-white">
                {selected.nom_competition}
              </h3>

              <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-500 dark:text-slate-400">
                <div>
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    Date :
                  </span>{" "}
                  {formatDateFR(selected.date_competition)}
                </div>

                <div>
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    Lieu :
                  </span>{" "}
                  {selected.lieu_competition || "Non renseigné"}
                </div>

                <div>
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    Niveau :
                  </span>{" "}
                  {selected.level_competition || "Non renseigné"}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[24px] border border-dashed border-slate-300 p-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Sélectionnez une compétition pour gérer ses photos de podiums.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function PhotosToolbar({ q, setQ, total, onReset, onCreate }) {
  return (
    <section className={`${CARD} p-4 sm:p-5`}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Photos enregistrées
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {total} résultat{total > 1 ? "s" : ""} affiché
            {total > 1 ? "s" : ""}.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            <FiX />
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onCreate}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700"
          >
            <FiPlus />
            Ajouter
          </button>
        </div>
      </div>

      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Rechercher par description, URL..."
          className={`${INPUT} pl-11`}
        />
      </div>
    </section>
  );
}

function PhotoCard({ photo, onEdit, onDelete, onPreview }) {
  const imageUrl = toAbsolutePhotoUrl(photo.photo_url) || FALLBACK_IMAGE;

  return (
    <article className="group overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => onPreview(imageUrl)}
        className="relative block h-64 w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-800"
      >
        <img
          src={imageUrl}
          alt={photo.description || "Photo de podium"}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          onError={(event) => {
            event.currentTarget.src = FALLBACK_IMAGE;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

        <div className="absolute bottom-4 left-4 right-4">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-800 backdrop-blur">
            <FiEye />
            Voir la photo
          </div>

          <h3 className="line-clamp-2 text-lg font-bold leading-tight text-white">
            {photo.description || "Photo de podium"}
          </h3>
        </div>
      </button>

      <div className="p-4">
        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/60">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">
            URL photo
          </div>

          <p className="line-clamp-2 break-all text-xs font-medium leading-relaxed text-slate-600 dark:text-slate-300">
            {photo.photo_url || "Non renseignée"}
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => onEdit(photo)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
          >
            <FiEdit2 />
            Modifier
          </button>

          <a
            href={imageUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2.5 text-sm font-bold text-indigo-700 transition hover:-translate-y-0.5 hover:bg-indigo-100 dark:bg-indigo-950/70 dark:text-indigo-300 dark:hover:bg-indigo-950"
          >
            <FiExternalLink />
            Ouvrir
          </a>

          <button
            type="button"
            onClick={() => onDelete(photo)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-3 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-rose-700"
          >
            <FiTrash2 />
            Supprimer
          </button>
        </div>
      </div>
    </article>
  );
}

function PhotosLoadingGrid() {
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="h-64 animate-pulse bg-slate-200 dark:bg-slate-800" />
          <div className="space-y-3 p-4">
            <div className="h-4 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-4 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-10 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>
      ))}
    </section>
  );
}

function EmptyState({ hasPhotos, onCreate, onReset }) {
  return (
    <section className="flex flex-col items-center justify-center rounded-[30px] border border-dashed border-slate-300 bg-white px-5 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FiCamera />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        {hasPhotos ? "Aucune photo ne correspond à la recherche" : "Aucune photo"}
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {hasPhotos
          ? "Essayez de modifier votre recherche pour retrouver une photo."
          : "Ajoutez vos premières photos de podiums pour cette compétition."}
      </p>

      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {hasPhotos ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            <FiX />
            Réinitialiser
          </button>
        ) : null}

        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
        >
          <FiPlus />
          Ajouter une photo
        </button>
      </div>
    </section>
  );
}

function NoCompetitionState() {
  return (
    <section className="flex flex-col items-center justify-center rounded-[30px] border border-dashed border-slate-300 bg-white px-5 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FiImage />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        Aucune compétition sélectionnée
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        Sélectionnez une compétition pour gérer ses photos de podiums.
      </p>
    </section>
  );
}

function Lightbox({ imageUrl, onClose }) {
  useEffect(() => {
    const onEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEscape);

    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-h-[92vh] max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-12 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
          aria-label="Fermer"
        >
          <FiX />
        </button>

        <img
          src={imageUrl}
          alt="Agrandissement"
          className="max-h-[92vh] max-w-full rounded-[28px] object-contain shadow-2xl"
          onError={(event) => {
            event.currentTarget.src = FALLBACK_IMAGE;
          }}
        />
      </div>
    </div>
  );
}